/**
 * Toggles a product in the guest wishlist. Shares the `bh:wishlist` localStorage
 * key and `wishlist:updated` event contract documented in assets/wishlist.js,
 * which owns the header badge count — this file only owns the per-product
 * toggle button's pressed state and the storage read/write.
 */
const STORAGE_KEY = 'bh:wishlist';

/** @returns {string[]} */
function getWishlist() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** @param {string[]} ids */
function setWishlist(ids) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // Storage unavailable (e.g. private browsing) — toggle state won't persist.
  }
}

class ProductWishlistButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.button?.addEventListener('click', this.#toggle);
    this.#syncPressedState();

    document.addEventListener('wishlist:updated', this.#syncPressedState);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#toggle);
    document.removeEventListener('wishlist:updated', this.#syncPressedState);
  }

  #syncPressedState = () => {
    if (!this.button) return;
    const handle = this.dataset.productHandle;
    const isSaved = getWishlist().includes(handle);

    this.button.setAttribute('aria-pressed', String(isSaved));
    this.button.setAttribute(
      'aria-label',
      isSaved ? this.dataset.removeLabel ?? '' : this.dataset.addLabel ?? ''
    );
  };

  #toggle = () => {
    const handle = this.dataset.productHandle;
    if (!handle) return;

    const wishlist = getWishlist();
    const index = wishlist.indexOf(handle);

    if (index === -1) {
      wishlist.push(handle);
    } else {
      wishlist.splice(index, 1);
    }

    setWishlist(wishlist);
    this.#syncPressedState();
    document.dispatchEvent(new CustomEvent('wishlist:updated'));
  };
}

if (!customElements.get('product-wishlist-button')) {
  customElements.define('product-wishlist-button', ProductWishlistButton);
}
